import { hideVisually, rem, rgba } from 'polished';
import styled, { css } from 'styled-components';

import Grid from '../Grid';
import {
  StyledLanguageNavButton as StyledLanguageNavButtonShared,
  StyledSharedHeader,
} from './Header.styles';
import { IStyledHeaderProps } from './Header.types';

const headerMobileHeight = '72px';

const StyledArrow = css`
  height: auto;
  width: ${rem('11px')};
`;

const StyledBox = css`
  position: absolute;
  right: 0;
  left: 0;
  z-index: ${({ theme }) => theme.layers.header};
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${headerMobileHeight});
  background-color: ${({ theme }) => theme.colors.main};
  overflow: hidden;

  &[aria-hidden='true'] {
    ${hideVisually()};
  }

  &[aria-hidden='false'] {
    &::before {
      position: absolute;
      top: ${rem('-8px')};
      right: 0;
      left: 0;
      height: ${rem('8px')};
      background-color: transparent;
      box-shadow: 0 0 ${rem('8px')} 0
        ${({ theme }) => rgba(theme.colors.cross.silverFox, 0.5)};
      content: '';
      pointer-events: none;
    }
  }
`;

export const StyledBody = styled.div`
  ${StyledBox}
  top: ${rem(headerMobileHeight)};
  justify-content: space-between;
  padding-top: ${rem('16px')};
`;

export const StyledOverlay = styled.div`
  ${StyledBox}
  top: 0;
  padding-top: ${rem('32px')};
  z-index: ${({ theme }) => theme.layers.mobileNavBody};
`;

export const StyledBodyGrid = styled(Grid)`
  flex-grow: 1;
  align-items: stretch;
`;

export const StyledHamburgerWrapper = styled.div`
  margin-right: ${rem('-8px')};
`;

export const StyledLogoWrapper = styled.div`
  width: ${rem('110px')};
`;

export const StyledTopStrip = styled.div`
  display: flex;
  height: ${rem(headerMobileHeight)};
  background-color: ${({ theme }) => theme.colors.main};
`;

export const StyledLanguageArrow = styled.span`
  margin-left: auto;
  pointer-events: none;

  svg {
    height: ${rem('11px')};
    width: auto;
    padding-right: ${rem('3px')};
  }
`;

export const StyledLanguageNav = styled.nav`
  display: flex;
  flex-direction: column;
  margin-top: auto;
  margin-bottom: ${rem('16px')};
  color: ${({ theme }) => theme.colors.cross.grayson};
  font-size: ${rem('14px')};
  text-transform: uppercase;
  position: fixed;
  bottom: 68px;
  left: 0;
  right: 0;
  padding: 0 ${rem('16px')};
`;

export const StyledLanguageNavButton = styled(StyledLanguageNavButtonShared)`
  &[aria-expanded='true'] {
    ${StyledLanguageArrow} {
      svg {
        transform: rotate(180deg);
      }
    }
  }
`;

export const StyledLanguageNavList = styled.ul`
  margin: 0;
  padding: 0;

  &[aria-hidden='true'] {
    ${hideVisually()};
  }
`;

export const StyledLanguageNavListItem = styled.li`
  list-style: none;
`;

export const StyledLanguageNavLink = styled.a`
  display: flex;
  padding: ${rem('8px')} 0 ${rem('8px')} ${rem('30px')};
  color: ${({ theme }) => theme.colors.cross.grayson};
  font-size: ${rem('16px')};
  font-weight: 400;
  line-height: 1.4;
  text-decoration: none;
  text-transform: uppercase;
`;

export const StyledNavLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem('16px')} ${rem('3px')} ${rem('16px')} 0;
  color: ${({ color, theme }) => (color ? color : theme.colors.cross.grayson)};
  font-size: ${rem('14px')};
  font-weight: 700;
  line-height: 1.4;
  text-decoration: none;
  text-transform: uppercase;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  svg {
    ${StyledArrow}
    transform: rotate(-90deg);
  }
`;

export const StyledPrimaryNav = styled.nav``;

export const StyledPrimaryNavList = styled.ul`
  flex-grow: 1;
  margin: 0;
  padding: 0;
`;

export const StyledPrimaryNavListItem = styled.li`
  list-style: none;
`;

export const StyledSecondaryNav = styled.nav`
  position: fixed;
  bottom: 0;
`;

export const StyledSecondaryNavList = styled.ul`
  display: flex;
  flex-grow: 1;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0;
  padding: ${rem('8px')} 0;
  border-top: 1px solid ${({ theme }) => theme.colors.cross.lightGray};
`;

export const StyledSecondaryNavListItem = styled.li`
  display: flex;
  flex-basis: 50%;
  justify-content: center;
  list-style: none;

  &:not(:first-child) {
    position: relative;

    &::before {
      position: absolute;
      top: 50%;
      left: 0;
      height: ${rem('20px')};
      margin-top: ${rem('-10px')};
      border-left: 1px solid ${({ theme }) => theme.colors.cross.lightGray};
      content: '';
    }
  }
`;

export const StyledBackButton = styled.button`
  display: flex;
  align-items: center;
  margin-bottom: ${rem('40px')};
  padding: 0;
  color: ${({ theme }) => theme.colors.cross.black};
  font-size: ${rem('16px')};
  font-weight: 400;
  line-height: 1.4;
  background-color: transparent;
  border: none;
  appearance: none;

  svg {
    ${StyledArrow}
    margin-right: ${rem('14px')};
    transform: rotate(90deg);
  }
`;

export const StyledOverlayTitle = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.cross.black};
  font-size: ${rem('14px')};
  font-weight: 700;
  line-height: 1.4;
  text-transform: uppercase;

  > span {
    margin-left: ${rem('8px')};
    font-size: ${rem('16px')};
    font-weight: 400;
    text-transform: none;
  }
`;

export const StyledOverlayNav = styled.nav``;

export const StyledOverlayNavList = styled.ul`
  margin: ${rem('24px')} 0 0 0;
  padding: 0;
`;

export const StyledOverlayNavListItem = styled.li`
  list-style: none;
  padding-left: ${rem('24px')};

  &:not(:last-child) {
    margin-bottom: ${rem('8px')};
  }
`;

export const StyledOverlayNavLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem('8px')} ${rem('3px')} ${rem('8px')} 0;
  color: ${({ theme }) => theme.colors.cross.grayson};
  font-size: ${rem('16px')};
  font-weight: 400;
  line-height: 1.4;
  text-decoration: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  svg {
    ${StyledArrow}
    transform: rotate(-90deg);
  }
`;

export const StyledHeader = styled.header<IStyledHeaderProps>`
  ${StyledSharedHeader}

  & > div > div {
    ${({ isFullWidth }) =>
      isFullWidth &&
      `
      margin: 0 ${rem('16px')};
      max-width: 100%;
    `};
  }

  ${StyledOverlayTitle} {
    span {
      color: ${({ theme, user }) => user && theme.colors[user].main};
    }
  }
`;
